/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/**
 *
 * WalletCards
 *
 */

import React, { memo, useState, useEffect, useContext } from 'react';
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import {
  Button,
  Text,
  Space,
  Toggle,
  Conditional,
} from '@cashfree-intl/coherent';
import CustomIcon from 'components/CustomIcons';
import {
  ACTIVATE_REFUND_WALLET,
  ACTIVE,
  ADD_TO_WALLET,
  DEACTIVATE_REFUND_WALLET,
  DISPUTE_WALLET,
  HOLD,
  REFUND_WALLET,
} from '../constants';
import { isWalletDisabled, toggleStateText } from '../helpers';
import WalletStateChangeConfirmation from 'components/WalletStateChangeConfirmation/WalletStateChangeConfirmation';
import AddToWallet from 'components/AddToWallet/AddToWallet';
import { CURRENCY_SYMBOL } from 'utils/constants';
import { IWalletCards } from '../types';
import {
  StyledRefreshText,
  StyledWalletCardDivision,
  StyledWalletCardPaper,
} from '../styled';
import InlineLoader from 'components/InlineLoader';

function WalletCards({
  walletsData: { refundWallet, disputeWallet },
  walletBalance,
  handleRefreshClick,
  handleAddMoneyToWalletClick,
  handleToggleChange,
  getWalletInfo,
  bankDetails: { refundWalletBankDetails, disputeWalletBankDetails },
  disputeWalletActive,
  refundsWalletActive,
  showDisputesWallet,
  bankList
}: IWalletCards) {
  const [activeModal, setActiveModal] = useState<string>('');
  const [activeModalData, setActiveModalData] = useState<any>({});

  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();

  const { pathname } = location;

  const [active, setActive] = useState({
    refundWallet: false,
    freeWallet: false,
    disputeWallet: false,
  });

  const [walletInactive, setIsWalletInActive] = useState({
    refundWallet: false,
    disputeWallet: showDisputesWallet,
  });

  useEffect(() => {
    setActive({ ...active, refundWallet: refundWallet?.status === ACTIVE, disputeWallet: disputeWallet.status === ACTIVE });
    setIsWalletInActive({ refundWallet: refundWallet?.status === 'IN_ACTIVE', disputeWallet: disputeWallet?.status === 'IN_ACTIVE' || showDisputesWallet });
  }, [refundWallet, disputeWallet]);

  const closeModal = () => {
    setActiveModal('');
    setActiveModalData({});
    getWalletInfo();
  };

  const handleModalConfirmation = () => {
    const { updatedToggleValue, walletType } = activeModalData;
    setActive(updatedToggleValue);
    const currentToggleStatus =
      updatedToggleValue[walletType] === true ? ACTIVE : HOLD;
    handleToggleChange(currentToggleStatus, walletType);
    setActiveModal('');
    setActiveModalData({});
  };

  const onToggle = (key: string) => (e: React.SyntheticEvent) => {
    e.stopPropagation();
    const updatedToggleValue = { ...active, [key]: !active[key] };
    if (active[key]) {
      setActiveModal(DEACTIVATE_REFUND_WALLET);
    } else {
      setActiveModal(ACTIVATE_REFUND_WALLET);
    }
    setActiveModalData({ updatedToggleValue, walletType: key });
  };

  const handleWalletCardClick = (walletType: string) => () => {
    const env = searchParams.get('env') || '';
    navigate(
      `${pathname}/wallet-details?walletType=${walletType}${env ? `&env=${env}` : ''
      }`,
    );
  };

  const cardSubheadingText = (key: string) => {
    if (key === DISPUTE_WALLET) return 'Keep sufficient funds in the wallet for recovery of disputes';
    return 'Avoid refund delays, keep your wallet funded at all times.';
  };

  const cardInfoText = (infoText: string, key: string) => {
    if (key === DISPUTE_WALLET) return 'Dispute amount is deducted from Dispute wallet';
    return `Refunds are processed from ${active[key] ? `${infoText} wallet` : 'unsettled balance'
      }`;
  };

  const addMoneyClickHandler = (walletType: string) => (
    e: React.SyntheticEvent,
  ) => {
    e.stopPropagation();
    setActiveModal(ADD_TO_WALLET);
    const bankDetails = walletType === 'Dispute Wallet' ? disputeWalletBankDetails : refundWalletBankDetails;
    setActiveModalData({ walletType, bankDetails });
    handleAddMoneyToWalletClick(walletType);
  };

  let walletCardsMetaData = [];

  if (disputeWalletActive && showDisputesWallet) {
    walletCardsMetaData = [...walletCardsMetaData, {
      key: DISPUTE_WALLET,
      heading: 'Dispute Wallet',
      iconName: 'WALLET_REFUND',
      infoText: 'Dispute',
    }];
  }

  if (refundsWalletActive) {
    walletCardsMetaData = [...walletCardsMetaData, {
      key: REFUND_WALLET,
      heading: 'Refund Wallet',
      iconName: 'WALLET_REFUND',
      infoText: 'Refund',
    }];
  }

  return (
    <>
      <Conditional
        if={
          activeModal === ADD_TO_WALLET && Object.keys(activeModalData.walletType === 'Dispute Wallet' ? disputeWalletBankDetails : refundWalletBankDetails).length > 1
        }
      >
        <AddToWallet
          open={activeModal === ADD_TO_WALLET}
          walletType={activeModalData?.walletType}
          closeModal={closeModal}
          isWalletsIntro={false}
          bankDetails={activeModalData.walletType === 'Dispute Wallet' ? disputeWalletBankDetails : refundWalletBankDetails}
          getWalletInfo={getWalletInfo}
          bankList={bankList}
        />
      </Conditional>

      <Conditional
        if={[ACTIVATE_REFUND_WALLET, DEACTIVATE_REFUND_WALLET].includes(
          activeModal,
        )}
      >
        <WalletStateChangeConfirmation
          activeModal={activeModal}
          walletType={activeModalData?.walletType?.substring(
            0,
            activeModalData?.walletType?.indexOf('Wallet'),
          )}
          closeModal={closeModal}
          handleModalConfirmation={handleModalConfirmation}
        />
      </Conditional>

      <Space gap={4} wrap="wrap">
        {walletCardsMetaData.map(card => {
          return (
            <StyledWalletCardPaper
              className="px-4 py-4 pointer"
              key={card.key}
              onClick={handleWalletCardClick(card.key)}
            >
              <Space justifyContent="space-between">
                <Space gap={1}>
                  <CustomIcon componentName={card.iconName} />
                  <Text
                    variant="h20"
                    color={active[card.key] ? 'body' : 'bodyLight'}
                  >
                    {card.heading}
                  </Text>
                </Space>
                <Space gap={1}>
                  {toggleStateText(active[card.key])}
                  <Toggle
                    active={active[card.key]}
                    onToggle={onToggle(card.key)}
                    disabled={isWalletDisabled(
                      card.key === 'refundWallet' ? walletBalance.refundWallet?.balance : walletBalance.disputeWallet?.balance,
                      active[card.key],
                    ) || walletInactive[card.key]  || card.key === DISPUTE_WALLET}
                  />
                </Space>
              </Space>
              <Text
                variant="b12"
                color={active[card.key] ? 'bodyLight' : 'placeholder'}
                className="mt-1 ml-4"
              >
                {cardSubheadingText(card.key)}
              </Text>
              <Conditional
                if={
                  !isWalletDisabled(
                    card.key === 'refundWallet' ? walletBalance.refundWallet?.balance : walletBalance.disputeWallet?.balance,
                    active[card.key],
                  )
                }
              >
                <Text
                  variant="b12"
                  className="ml-4 mt-1"
                  color={active[card.key] ? 'body' : 'bodyLight'}
                >
                  {cardInfoText(card.infoText, card.key)}
                </Text>
              </Conditional>
              <Conditional
                if={
                  isWalletDisabled(
                    walletBalance?.refundWallet?.balance,
                    active[card.key],
                  ) && !walletInactive[card.key]
                }
              >
                <Text variant="b12" className="ml-4 mt-1" color="warning">
                  Recharge a minimum of {CURRENCY_SYMBOL.INR}5000 to activate
                  the wallet.
                </Text>
              </Conditional>
              <StyledWalletCardDivision className="mb-2 mt-2" />
              <div className="ml-4">
                <Text variant="b12" color="bodyLight" className="mb-1">
                  Wallet Balance
                </Text>
                <Space justifyContent="space-between">
                  <InlineLoader
                    loading={
                      !Object.keys(walletBalance?.refundWallet || {}).length
                    }
                    content={
                      <Text
                        variant="h28"
                        color={active[card.key] ? 'body' : 'placeholder'}
                      >
                        {CURRENCY_SYMBOL.INR}{' '}
                        {card.key === 'refundWallet' ? walletBalance.refundWallet?.balance : walletBalance.disputeWallet?.balance || '0'}
                      </Text>
                    }
                  />
                  <Space
                    gap={1}
                    alignItems="center"
                    onClick={handleRefreshClick(card.key)}
                  >
                    <CustomIcon componentName="REFRESH" />
                    <StyledRefreshText className="b12" color="primary">
                      Refresh
                    </StyledRefreshText>
                  </Space>
                </Space>
                <Button
                  primary
                  size="small"
                  className="mt-2"
                  onClick={addMoneyClickHandler(card.heading)}
                  disabled={walletInactive[card.key]}
                  data-event-name="Add_Money"
                  data-ignore-inner-text="true"
                >
                  Add money
                </Button>
              </div>
            </StyledWalletCardPaper>
          );
        })}
      </Space>
    </>
  );
}

export default memo(WalletCards);
